import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";
import { stockSearch, getWatchlistItems } from "../../../api/user";
import { baseSymbol, pairSymbol, instrumentName } from "../../../utils/instrumentFormat";
import { chartSymbolFor } from "../../Watchlist/watchlistFormat";
import AssetAvatar from "../../Watchlist/AssetAvatar";
import ListSkeleton from "../../Skeleton/ListSkeleton";
import { useLeagueTrade } from "../../../context/LeagueTradeContext";
import {
  filterEligible,
  isEligibleInLeague,
  leagueHasCustomList,
  leagueSearchTabs,
  marketOfSymbol,
} from "../../../utils/leagueAssets";

// Stocks + Crypto are the primary classes; Forex sits last (least used).
const CATEGORIES = [
  { key: "Stocks", label: "Stocks" },
  { key: "Crypto", label: "Crypto" },
  { key: "Forex", label: "Forex" },
];

// Before the user types, each tab browses QUSHi's own curated category list —
// the same `default_asset_type` rows (with their brand logos) that back the
// Watchlist screen's Add Assets panel. An empty-query stockSearch used to fill
// this space with whatever the provider returned alphabetically ("AAAA",
// "AAAC"…), which is noise.
const CATEGORY_KEY = { Stocks: "stocks", Crypto: "crypto", Forex: "forex" };
// The inverse, so a custom league's own symbols can be dealt onto the right tab
// using the SAME prefix rule the eligibility gate uses. One rule, one place.
const TAB_OF_MARKET = { stocks: "Stocks", crypto: "Crypto", forex: "Forex" };
const marketTabOf = (symbol) => TAB_OF_MARKET[marketOfSymbol(symbol)] || "Stocks";
// Every row is price-enriched server-side (a Polygon round trip each), so keep
// the page small and let "Load more" fetch the next few.
const PREVIEW_LIMIT = 6;

const SymbolSearchModal = ({ open, onClose, onPick }) => {
  // Inside a league this is the league's record; on the main Trade screen it is
  // null and everything below behaves exactly as it did before.
  const league = useLeagueTrade();
  const game = league?.game || null;

  // Client: "Only the tabs the league organizer chooses appear. Stock, crypto,
  // forex - if the organizer chooses mixed, show all three tabs."
  const allowedTabs = useMemo(() => leagueSearchTabs(game), [game]);
  const categories = useMemo(
    () => CATEGORIES.filter((c) => allowedTabs.includes(c.key)),
    [allowedTabs]
  );

  const [activeCat, setActiveCat] = useState(allowedTabs[0] || "Stocks");
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  // Curated browse list for the active tab (shown while the query is empty).
  const [preview, setPreview] = useState([]);
  const [previewPage, setPreviewPage] = useState(1);
  const [previewHasMore, setPreviewHasMore] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewReqRef = useRef(0);

  const trimmedTerm = term.trim();
  const isSearchMode = trimmedTerm.length > 0;

  useEffect(() => {
    if (open) {
      setTerm("");
      setResults([]);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // "Stocks" is the default, and a crypto-only league does not offer it. Without
  // this the modal would open on a tab that is not on screen and browse a market
  // the league cannot trade.
  useEffect(() => {
    if (!allowedTabs.includes(activeCat)) setActiveCat(allowedTabs[0] || "Stocks");
  }, [allowedTabs, activeCat]);

  const loadPreview = useCallback(async (cat, page) => {
    const key = CATEGORY_KEY[cat] || "stocks";
    const reqId = ++previewReqRef.current;

    // "The organizer picks a customer list; we only show the custom list."
    //
    // A custom league has no market to browse — it has an enumerated set. Asking
    // the curated category endpoint for one would return QUSHi's own rows, of
    // which the league may allow none, and the browse space would come up empty
    // for no visible reason. So the list IS the browse list, and no request is
    // made at all. Same shape MobileChartToolbar already builds for its wheel.
    if (leagueHasCustomList(game)) {
      const rows = (league?.symbols || []).map((symbol) => ({
        symbol,
        watchlist_name: symbol,
      }));
      setPreview(rows.filter((row) => marketTabOf(row.symbol) === cat));
      setPreviewPage(1);
      setPreviewHasMore(false);
      setPreviewLoading(false);
      return;
    }

    setPreviewLoading(true);
    try {
      const r = await getWatchlistItems(key, page, PREVIEW_LIMIT);
      if (reqId !== previewReqRef.current) return;
      const raw = Array.isArray(r?.data?.items) ? r.data.items : [];
      // Belt and braces: the curated rows for a category are all of that market,
      // so this normally removes nothing — but the "Bitcoin" row trades as an
      // ETF, and filterEligible tests the symbol an ORDER would carry, which is
      // the same question the Buy button asks.
      const its = filterEligible(raw, game);
      setPreview((prev) => (page === 1 ? its : [...prev, ...its]));
      setPreviewPage(page);
      setPreviewHasMore(r?.data?.pagination?.has_more ?? raw.length === PREVIEW_LIMIT);
    } catch (e) {
      if (reqId === previewReqRef.current && page === 1) setPreview([]);
    } finally {
      if (reqId === previewReqRef.current) setPreviewLoading(false);
    }
  }, [game, league]);

  // (Re)load the browse list when the modal opens or the tab changes. Requests
  // are only made while the modal is open — a closed modal fetches nothing.
  useEffect(() => {
    if (!open) return;
    setPreview([]);
    setPreviewPage(1);
    setPreviewHasMore(false);
    loadPreview(activeCat, 1);
  }, [open, activeCat, loadPreview]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    // No query → the curated browse list owns the space; don't search for "".
    if (!isSearchMode) {
      setResults([]);
      setLoading(false);
      return;
    }
    let active = true;
    const controller = new AbortController();
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await stockSearch(
          { category: activeCat, query: trimmedTerm },
          controller.signal
        );
        if (!active) return;
        const payload = res?.data || [];
        const rows = Array.isArray(payload) ? payload : [];
        // THE DEAD END, closed. The provider answers about the whole market; the
        // league decides what may be shown. Outside a league isEligibleInLeague
        // returns true for everything, so the main screen is untouched.
        setResults(rows.filter((row) => isEligibleInLeague(game, row.ticker || row.symbol)));
      } catch (e) {
        if (active && e?.name !== "CanceledError") setResults([]);
      } finally {
        if (active) setLoading(false);
      }
    }, 250);
    return () => {
      active = false;
      controller.abort();
      clearTimeout(t);
    };
  }, [trimmedTerm, isSearchMode, activeCat, open]);

  if (!open) return null;

  const handlePick = (item) => {
    // Search results carry `ticker`; curated rows carry `symbol` (plus an `etf`
    // proxy for index rows — "S&P 500" charts as SPY).
    const symbol = item.ticker || chartSymbolFor(item);
    if (!symbol) return;
    onPick?.({ symbol, category: activeCat });
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center bg-black/40 pt-20"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-3xl mx-4 flex flex-col"
        // style={{ maxHeight: "80vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-base font-semibold text-gray-800">Symbol search</h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 text-gray-500"
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Search input */}
        <div className="px-4 pt-3">
          <div className="flex items-center gap-2 border border-gray-300 rounded-full px-4 py-2 focus-within:border-primaryColor">
            <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Search..."
              className="flex-1 outline-none text-sm bg-transparent"
            />
            {term && (
              <button
                type="button"
                onClick={() => setTerm("")}
                className="text-gray-400 hover:text-gray-600"
                title="Clear"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
        </div>

        {/* Category tabs */}
        <div className="flex items-center gap-2 px-4 py-3 border-b overflow-x-auto">
          {categories.map((c) => (
            <button
              type="button"
              key={c.key}
              onClick={() => setActiveCat(c.key)}
              className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                activeCat === c.key
                  ? "bg-gray-800 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Results — the curated browse list until the user types, then matches.
            Both use the SAME row geometry (32px avatar slot, text-sm title,
            text-xs subtitle) so typing doesn't reflow the list. */}
        <div className="overflow-y-auto min-h-[40px] max-h-[500px]">
          {!isSearchMode && (
            <>
              {preview.map((item, idx) => {
                const sym = chartSymbolFor(item);
                const chip = baseSymbol(sym, activeCat);
                const title = pairSymbol(sym);
                // Prefer the enriched provider name; fall back to the curated
                // hand-written label (instrumentName would otherwise resolve an
                // un-enriched row to its own symbol, repeating the title line).
                const rawName = item.details?.stock_name || item.watchlist_name;
                const name = instrumentName({
                  name: rawName,
                  symbol: sym,
                  category: activeCat,
                });
                return (
                  <button
                    type="button"
                    key={`preview-${sym}-${idx}`}
                    onClick={() => handlePick(item)}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3 border-b border-gray-50"
                  >
                    {/* Brand art at the same 32px footprint as the search rows'
                        chips, so the two lists stay aligned. */}
                    <AssetAvatar
                      logo={item.logo}
                      chip={chip}
                      className="w-8 h-8"
                      chipTextClass="text-[10px] font-bold"
                      chipClassName="bg-gray-200 text-gray-700"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-gray-800">{title}</div>
                      <div className="text-xs text-gray-500 truncate">{name}</div>
                    </div>
                    <span className="text-[10px] font-semibold text-gray-400 uppercase flex-shrink-0">
                      {activeCat}
                    </span>
                  </button>
                );
              })}
              {previewLoading && (
                <ListSkeleton
                  rows={preview.length === 0 ? 6 : 2}
                  avatarSize={32}
                  columns={0}
                  divided={false}
                  rowClassName="px-4 py-3"
                  label="Loading assets"
                />
              )}
              {!previewLoading && preview.length === 0 && (
                <div className="px-4 py-6 text-sm text-gray-500 text-center">
                  Type to search
                </div>
              )}
              {!previewLoading && previewHasMore && (
                <div className="flex justify-center py-3">
                  <button
                    type="button"
                    onClick={() => loadPreview(activeCat, previewPage + 1)}
                    className="text-sm font-medium text-primaryColor"
                  >
                    Load more
                  </button>
                </div>
              )}
            </>
          )}
          {/* One loading idiom for the whole modal: the same placeholder rows the
              browse list uses, so switching between browse and search never
              swaps a skeleton for a line of text. */}
          {isSearchMode && loading && (
            <ListSkeleton
              rows={6}
              avatarSize={32}
              columns={0}
              divided={false}
              rowClassName="px-4 py-3"
              label="Searching"
            />
          )}
          {isSearchMode && !loading && results.length === 0 && (
            <div className="px-4 py-6 text-sm text-gray-500 text-center">
              No matches
            </div>
          )}
          {isSearchMode &&
            !loading &&
            results.map((item, idx) => {
              const sym = item.ticker || item.symbol;
              // Chip = base asset symbol (BTC), title = pair ticker (BTCUSD),
              // subtitle = title-cased instrument name (system-wide rule).
              const chip = baseSymbol(sym, activeCat);
              const shortName = item.short_name || pairSymbol(sym);
              const displayName = instrumentName({ name: item.name, symbol: sym, category: activeCat });
              return (
                <button
                  type="button"
                  key={`${sym}-${idx}`}
                  onClick={() => handlePick(item)}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3 border-b border-gray-50"
                >
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-700 flex-shrink-0">
                    {chip}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-gray-800">{shortName}</div>
                    {item.name && (
                      <div className="text-xs text-gray-500 truncate">
                        {displayName}
                      </div>
                    )}
                  </div>
                  <span className="text-[10px] font-semibold text-gray-400 uppercase flex-shrink-0">
                    {activeCat}
                  </span>
                </button>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default SymbolSearchModal;
